// Total de la mesa tras «Comandar»/«Guardar» con el precio del SERVIDOR.
//
// Al comandar, el TPV suma al instante a `mesa.cuenta` el total de su
// carrito (optimista). El servidor puede tarifar distinto (promoción
// horaria, suplemento por zona, línea «Suplemento …» por ticket) y suma
// a la cuenta SU total; el eco de tiempo real de mi propia comanda se
// descarta, así que sin esto la tile de la mesa, el total del checkout,
// la sesión y el cobro se quedaban con el precio del carrito hasta recargar.
//
// Respuesta de `pedidos/add/accept/payment`:
//   { order: { precio, serverRepriced, … }, checkout: null | { session, checkout } }
// `checkout` (mismo shape que GET data/getCheckout) solo viene cuando el
// servidor retarificó (`order.serverRepriced`). Con él se sustituye el
// carrito/pedidos/sesión de la mesa abierta; sin él (API antigua) se ajusta
// la cuenta de la mesa con la diferencia `order.precio − total del carrito`.

import { updateMesaSuccess } from "src/redux/slices/mesas";
import { syncCartCheckoutComplete } from "src/redux/slices/product";
import { getOrdersSuccess } from "src/redux/slices/order";
import { getSessionSuccess } from "src/redux/slices/session";
import { activeMesaId } from "src/redux/middleware/cartDraftMiddleware";

const num = (v) => {
  const n = Number(v);
  return v != null && v !== "" && Number.isFinite(n) ? n : null;
};
const round2 = (x) => Math.round(x * 100) / 100;

const orderOf = (response) => response?.data?.order ?? response?.data?.Order ?? null;

/** Total del pedido según el servidor (o null si la respuesta no lo trae). */
export function serverComandaTotal(response) {
  const order = orderOf(response);
  if (!order || typeof order !== "object") return null;
  return num(order.precio ?? order.Precio);
}

/**
 * Checkout del servidor que trae la respuesta de la comanda (`{ session,
 * checkout }`) o null. Defensivo: camelCase/PascalCase.
 */
export function serverComandaCheckout(response) {
  const raw = response?.data?.checkout ?? response?.data?.Checkout ?? null;
  if (!raw || typeof raw !== "object") return null;
  const session = raw.session ?? raw.Session ?? null;
  const checkout = raw.checkout ?? raw.Checkout ?? null;
  if (!checkout || typeof checkout !== "object" || !Array.isArray(checkout.cart ?? checkout.Cart)) return null;
  return { session, checkout: checkout.cart ? checkout : { ...checkout, cart: checkout.Cart } };
}

/** Cuenta de la mesa según el checkout del servidor (o null). */
export function serverCheckoutCuenta(server) {
  if (!server) return null;
  const fromSession = num(server.session?.cuenta ?? server.session?.Cuenta);
  if (fromSession != null) return fromSession;
  const c = server.checkout;
  const fromTotals = num(c.subtotal ?? c.Subtotal ?? c.total ?? c.Total);
  if (fromTotals != null) return fromTotals;
  return round2(
    (c.cart || []).reduce((s, l) => s + (Number(l?.precio) || 0) * (Number(l?.quantity ?? l?.unidades) || 0), 0),
  );
}

/** Diferencia servidor − carrito (0 si no hay dato o es despreciable). */
export function comandaPriceDelta(response, clientTotal) {
  const server = serverComandaTotal(response);
  const client = num(clientTotal);
  if (server == null || client == null) return 0;
  const d = round2(server - client);
  return Math.abs(d) < 0.005 ? 0 : d;
}

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

// La ficha del plano (`mesas`) primero: es la que actualizan los eventos de
// tiempo real. `mesa` (la seleccionada) puede ser una copia vieja si ya se
// salió de la mesa, y reescribirla devolvía campos caducados al plano (p. ej.
// el badge «cuenta impresa» que el eco de la comanda acababa de apagar).
function findMesa(st, mesaId) {
  return (
    (st.mesas || []).find((m) => sameId(m?.id, mesaId)) ||
    (sameId(st.mesa?.id, mesaId) ? st.mesa : undefined)
  );
}

/**
 * Tras comandar: totales con el precio del servidor.
 *  • Con checkout del servidor en la respuesta: si la mesa sigue abierta en
 *    este TPV, sustituye carrito comandado / pedidos / sesión (las líneas
 *    sin comandar se conservan) y fija `mesa.cuenta` a la del servidor.
 *  • Sin él: ajusta `mesa.cuenta` con la diferencia servidor − carrito.
 * Devuelve la diferencia aplicada a la cuenta (0 si ninguna).
 */
export function reconcileMesaCuentaAfterComanda(mesaId, clientTotal, response) {
  return (dispatch, getState) => {
    if (mesaId == null) return 0;
    const st0 = getState() || {};
    const server = serverComandaCheckout(response);

    if (server) {
      // Solo si el carrito en pantalla es el de esa mesa (como SyncCart).
      const selected = sameId(activeMesaId(st0), mesaId);
      if (selected) {
        const session = server.session;
        if (session && Array.isArray(session.pedidos)) dispatch(getOrdersSuccess(session.pedidos));
        dispatch(syncCartCheckoutComplete(server.checkout));
        if (session) dispatch(getSessionSuccess(session));
      }
      const cuenta = serverCheckoutCuenta(server);
      const current = findMesa(getState()?.mesas || {}, mesaId);
      if (cuenta == null || !current) return 0;
      const prev = Number(current.cuenta) || 0;
      if (Math.abs(prev - cuenta) < 0.005) return 0;
      dispatch(updateMesaSuccess({ ...current, cuenta }));
      return round2(cuenta - prev);
    }

    const delta = comandaPriceDelta(response, clientTotal);
    if (!delta) return 0;
    const current = findMesa(st0.mesas || {}, mesaId);
    if (!current) return 0;
    const cuenta = Math.max(0, round2((Number(current.cuenta) || 0) + delta));
    dispatch(updateMesaSuccess({ ...current, cuenta }));
    return delta;
  };
}
